import React, { useState, useEffect } from 'react'
import { Settings, X, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'

const SETTINGS_KEY = 'worldSimulatorSettings'

// 默认设置
const defaultSettings = {
  nsfwEnabled: false,
  outputLength: 'medium', // 'short', 'medium', 'long', 'very-long'
  showWorldInfo: true,
  showCharacterInfo: true,
  showStatusInfo: true
}

// 输出长度配置
export const outputLengthConfig = {
  short: { min: 50, max: 150, label: '简短 (50-150字)' },
  medium: { min: 150, max: 400, label: '中等 (150-400字)' },
  long: { min: 400, max: 800, label: '较长 (400-800字)' },
  'very-long': { min: 800, max: 1500, label: '很长 (800-1500字)' }
}

/**
 * 获取世界模拟器设置
 */
export const getWorldSimulatorSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY)
    if (saved) {
      return { ...defaultSettings, ...JSON.parse(saved) }
    }
  } catch (error) {
    console.error('读取设置失败:', error)
  }
  return defaultSettings
}

/**
 * 保存世界模拟器设置
 */
export const saveWorldSimulatorSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
    return true
  } catch (error) {
    console.error('保存设置失败:', error)
    return false
  }
}

/**
 * 世界模拟器设置对话框组件
 */
export const WorldSimulatorSettingsDialog = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState(getWorldSimulatorSettings())

  useEffect(() => {
    if (isOpen) {
      setSettings(getWorldSimulatorSettings())
    }
  }, [isOpen])

  const handleSave = () => {
    if (saveWorldSimulatorSettings(settings)) {
      toast.success('设置已保存')
      onClose()
    } else {
      toast.error('保存设置失败')
    }
  }

  const handleReset = () => {
    setSettings(defaultSettings)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-dark-800 rounded-xl p-6 max-w-2xl w-full mx-4 border border-dark-600 max-h-[90vh] overflow-y-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-white">世界模拟器设置</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* 设置内容 */}
        <div className="space-y-6">
          {/* NSFW模式 */}
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">NSFW模式</h3>
                <p className="text-sm text-dark-300">
                  开启后允许生成成人向内容。请确保你已年满18岁。
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.nsfwEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, nsfwEnabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-dark-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            {settings.nsfwEnabled && (
              <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-400">
                    已启用NSFW模式。生成的内容可能包含成人向内容，请谨慎使用。
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* AI输出长度 */}
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
            <h3 className="text-lg font-semibold text-white mb-3">AI输出长度</h3>
            <p className="text-sm text-dark-300 mb-4">
              控制AI生成内容的长度范围
            </p>
            <div className="space-y-2">
              {Object.entries(outputLengthConfig).map(([key, config]) => (
                <label
                  key={key}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    settings.outputLength === key
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'bg-dark-800 border-dark-600 text-white hover:border-dark-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="outputLength"
                    value={key}
                    checked={settings.outputLength === key}
                    onChange={(e) => setSettings(prev => ({ ...prev, outputLength: e.target.value }))}
                    className="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                  />
                  <span className="flex-1">{config.label}</span>
                  <span className="text-xs text-dark-400">
                    {config.min}-{config.max}字
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* 信息显示控制 */}
          <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
            <h3 className="text-lg font-semibold text-white mb-3">信息显示控制</h3>
            <p className="text-sm text-dark-300 mb-4">
              控制AI输出中是否包含以下信息
            </p>
            <div className="space-y-3">
              {/* 世界信息 */}
              <label className="flex items-center justify-between p-3 bg-dark-800 rounded-lg border border-dark-600 hover:border-dark-500 transition-colors">
                <div>
                  <div className="text-white font-medium">显示世界信息</div>
                  <div className="text-xs text-dark-400 mt-1">
                    在输出中包含世界设定、地区、规则等信息
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.showWorldInfo}
                    onChange={(e) => setSettings(prev => ({ ...prev, showWorldInfo: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-dark-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </label>

              {/* 角色信息 */}
              <label className="flex items-center justify-between p-3 bg-dark-800 rounded-lg border border-dark-600 hover:border-dark-500 transition-colors">
                <div>
                  <div className="text-white font-medium">显示角色信息</div>
                  <div className="text-xs text-dark-400 mt-1">
                    在输出中包含角色状态、属性、关系等信息
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.showCharacterInfo}
                    onChange={(e) => setSettings(prev => ({ ...prev, showCharacterInfo: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-dark-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </label>

              {/* 状态信息 */}
              <label className="flex items-center justify-between p-3 bg-dark-800 rounded-lg border border-dark-600 hover:border-dark-500 transition-colors">
                <div>
                  <div className="text-white font-medium">显示状态信息</div>
                  <div className="text-xs text-dark-400 mt-1">
                    在输出中包含数值系统、量化状态等信息
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.showStatusInfo}
                    onChange={(e) => setSettings(prev => ({ ...prev, showStatusInfo: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-dark-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </label>
            </div>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-dark-700">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
          >
            重置为默认
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

